import { UniButton } from '@/design-system/components/buttons';
import { UniIconButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme, SafeAreaView, ScrollView, Pressable } from 'dripsy';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { PageHeaderModalNative } from '@/features/account-management/screens/page/PageHeaderModalNative';
import { RootStackRoutes } from 'types';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { Icon } from '@/design-system/components/icons/Icon';
import { useReportingTransactions } from '@/features/account-management/screens/list/hooks/useListAccounts.hook';
import { CreateCardAccountStackRoutes } from '@/features/card-account/screens/create-card-account/type';
import { LayoutContext } from 'contexts/LayoutContext';
import { isEmpty } from 'lodash';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { Linking } from 'react-native';
import moment from 'moment-timezone';
import { CardAccountTypeFilters } from '@/features/card-account/options';
import { CardAccountTypeColor } from '@/features/card-account/enum';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { CardAccountStackRoutes } from '@/features/card-account/card.navigator';

export interface DownloadCardAccountTransactionReportProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  creditLineId?: string;
}

export const DownloadCardAccountTransactionReportNativeScreen: React.FC<DownloadCardAccountTransactionReportProps> = (props) => {
  const { onClose, onLayout, creditLineId } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { container, body, textMb, btnView, btn, account } = sxStyles;

  const navigationAny = useNavigation<any>();

  const [wallet, setWallet] = useState<any>();

  const [filterValues, setFilterValues] = useState<any>();
  const [alertMessage, setAlertMessage] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { reportingCardaccountsTransactions } = useReportingTransactions();
  const downloadTransactionReportFunc = () => {
    setIsLoading(true);
    reportingCardaccountsTransactions({
      dateRangeFrom: filterValues?.fromDate,
      dateRangeTo: filterValues?.toDate,
      creditLineId: creditLineId || wallet?.id
    })
      .then((response) => {
        setIsLoading(false);
        if (response.data && response.data?.url) {
          // Linking.openURL(response.data?.url);
          const newWindow = window.open(response.data?.url, '_blank');
          if (newWindow) newWindow.opener = null;
          onClose();
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setAlertMessage(error);
      });
  };

  const { theme } = useDripsyTheme();

  return (
    <SafeAreaView sx={container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View sx={{ height: '100%', backgroundColor: '$white' }}>
          <PageHeaderModalNative>
            <View sx={{ px: '$4', mt: '$5', mb: '$4' }}>
              <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
                <UniIconButton
                  icon="arrowLeft"
                  options={{ iconStyles: { color: theme?.colors?.$white as string } }}
                  onPress={onClose}></UniIconButton>
              </View>
              <View sx={{ textAlign: 'center' }}>
                <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
                  {t('card-account-management::account-page.button--download-transaction-report')}
                </TextTitle>
              </View>
            </View>
          </PageHeaderModalNative>
          <View sx={body}>
            <TextTitle sx={textMb} semiBold>
              {t('card-account-management::account-page.text--download-following-filter')}
            </TextTitle>
            <View sx={{ gap: '$5' }}>
              {!creditLineId ? (
                <View sx={{ gap: 4 }}>
                  <TextCaption semiBold>{t('card-account-management::account-page.text--account')}</TextCaption>
                  <Pressable
                    onPress={() => {
                      navigationAny.navigate(creditLineId ? RootStackRoutes.AccountManagement : RootStackRoutes.CardAccount, {
                        screen: AccountManagementStackRoutes.DownloadTransactionReport,
                        params: {
                          screen: CreateCardAccountStackRoutes.SelectCardAccount,
                          onLayout,
                          onSelect: (data: any) => {
                            setWallet(data);
                          }
                        }
                      });
                    }}>
                    <View sx={{ ...account, ...(wallet ? { flexDirection: 'column', py: '$2' } : {}) }}>
                      <View
                        sx={{
                          justifyContent: 'space-between',
                          display: 'flex',
                          width: '100%',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '$1'
                        }}>
                        {wallet ? (
                          <View sx={{ gap: '$1' }}>
                            <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                              <TextBody semiBold sx={{ color: '$textPrimary' }}>
                                {wallet?.label}
                              </TextBody>
                              <TextBody semiBold sx={{ color: '$textPrimary' }}>
                                {formatAmountByDecimals({ value: Math.abs(wallet?.balance), suffix: wallet?.assetSymbol })}{' '}
                                {wallet?.balance < 0 && '[CR]'}
                              </TextBody>
                            </View>
                            <View sx={{ flexDirection: 'row', gap: '$2', alignItems: 'center' }}>
                              {wallet?.autopayEnabled && (
                                <View
                                  sx={{
                                    paddingX: '$2',
                                    paddingY: '$1',
                                    width: 'fit-content',
                                    borderRadius: 100,
                                    backgroundColor: theme.colors?.$tagGreenBackground as string
                                  }}>
                                  <TextCaption sx={{ color: theme.colors?.$tagGreenText as string }}>
                                    {t('card-account::landing-page.text--utgl-autopay-enabled')}
                                  </TextCaption>
                                </View>
                              )}
                              <View
                                sx={{
                                  paddingX: '$2',
                                  paddingY: '$1',
                                  width: 'fit-content',
                                  borderRadius: 100,
                                  backgroundColor: theme.colors?.$tagBlackBackground as string
                                }}>
                                <TextCaption sx={{ color: theme.colors?.$tagBlackText as string }}>{wallet?.assetSymbol}</TextCaption>
                              </View>
                              <View
                                sx={{
                                  paddingX: '$2',
                                  paddingY: '$1',
                                  width: 'fit-content',
                                  borderRadius: 100,
                                  backgroundColor: theme.colors[
                                    `$tag${CardAccountTypeColor[wallet?.type as ColorTypes]}Background`
                                  ] as string
                                }}>
                                <TextCaption
                                  sx={{ color: theme.colors[`$tag${CardAccountTypeColor[wallet?.type as ColorTypes]}Text`] as string }}>
                                  {CardAccountTypeFilters(t).find((rec) => rec.value === wallet?.type)?.label}
                                </TextCaption>
                              </View>
                            </View>
                          </View>
                        ) : (
                          <TextCaption sx={{ color: '$borderUpload' }}>
                            {t('card-account::credit-card-new-account.glossary.select-account')}
                          </TextCaption>
                        )}
                        <View sx={{ flexDirection: 'row', gap: '$1' }}>
                          <Icon name="arrowRight" />
                          {wallet ? (
                            <UniIconButton
                              icon="cross"
                              onPress={() => {
                                setWallet(undefined);
                              }}
                            />
                          ) : undefined}
                        </View>
                      </View>
                    </View>
                  </Pressable>
                </View>
              ) : undefined}
              <View sx={{ gap: 4 }}>
                <TextCaption semiBold>{t('earn-invest::glossary.date-range')}</TextCaption>
                <UniIconButton
                  sx={{ alignItems: 'start' }}
                  icon="historyCalendar"
                  options={{ iconStyles: { color: theme?.colors?.$white as string } }}
                  onPress={() => {
                    navigationAny.navigate(creditLineId ? RootStackRoutes.AccountManagement : RootStackRoutes.CardAccount, {
                      screen: AccountManagementStackRoutes.BottomSheetDatePicker,
                      params: {
                        value: {
                          from: filterValues?.fromDate ? moment(filterValues?.fromDate) : undefined,
                          to: filterValues?.toDate ? moment(filterValues?.toDate) : undefined
                        },
                        onChange: (value: any) => {
                          setFilterValues({
                            ...filterValues,
                            fromDate: value.from ? value.from?.toDate() : undefined,
                            toDate: value.to ? value.to?.toDate() : undefined
                          });
                        }
                      }
                    });
                  }}></UniIconButton>
              </View>
            </View>
            {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
            <View sx={btnView}>
              <UniButton
                sx={btn}
                labelSx={{ color: '$balck' }}
                label={t('card-account-management::account-page.text--download')}
                onPress={downloadTransactionReportFunc}
                loading={isLoading}
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    height: '100%',
    width: '100%',
    backgroundColor: '$backgroundPrimary',
    zIndex: -100
  },
  body: {
    px: 16,
    py: 20
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    width: '100%',
    left: 0,
    bottom: 0,
    pt: '$10'
  },
  btn: {
    width: '100%',
    height: 44
  },
  account: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    py: '$5',
    px: 6,
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderRadius: 5
  }
};
